import { FC, HTMLAttributes, useEffect } from "react";
import { ReactionCard } from "../ReactionCard";
import { motion } from "framer-motion";
import { REACTIONS_TYPES } from "@shared/constants";
import emptyStateGif from "@assets/lottie/emptyState.json";
import { Player } from "@lottiefiles/react-lottie-player";
import { useReactionsPagination } from "@pages/reactions/hook";
import { useNavigate } from "react-router-dom";
import { Loader } from "@widgets/Loader";

import styles from "./ReactionsList.module.scss";

const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
      staggerChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

interface ReactionsListProps extends HTMLAttributes<HTMLDivElement> {
  reactionsType: REACTIONS_TYPES;
}

export const ReactionsList: FC<ReactionsListProps> = ({
  className,
  style,
  reactionsType,
}) => {
  const navigate = useNavigate();

  const { reactions, loading, error, isError, listEndRef } =
    useReactionsPagination(reactionsType);
  const users = reactions?.data || [];

  useEffect(() => {
    if (error && isError) {
      console.error(error);
      navigate("/unknownError");
      return;
    }
  }, [error, isError, navigate]);

  if (loading && !reactions) return <Loader />;

  return (
    <motion.div
      key={reactionsType}
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={`${className && className} ${styles.tabPanel}`}
      style={style}
    >
      {users.length > 0 ? (
        users.map((el, idx) => (
          <motion.div variants={itemVariants} key={idx}>
            <ReactionCard user={el} reactionType={reactionsType} />
          </motion.div>
        ))
      ) : loading ? (
        <Loader />
      ) : (
        <motion.div variants={itemVariants} className={styles.emptyState}>
          <Player
            autoplay
            loop
            src={emptyStateGif}
            className={styles.emptyStateImage}
          />
          <p className={styles.emptyStateText}>
            {reactionsType === REACTIONS_TYPES.Likes
              ? "Тут пока пусто, но скоро появятся лайки! 😊"
              : "Пока ваше окружение пустует, но мы уже подбираем для вас интересных людей по вашим увлечениям! 🚀"}
          </p>
        </motion.div>
      )}
      <div ref={listEndRef} style={{ height: "20px" }} />
    </motion.div>
  );
};
